/* eslint-disable react-refresh/only-export-components */
import type { MealVoucher } from 'app/src/model'
import { mealVouchersApi, membershipApi, type portalConfigurationApi } from 'app/src/services/welfareportal'
import { activeCampaigns, getFromSessionStorage, setToSessionStorage } from 'app/src/shared/helpers/funcutils'
import { HttpClientType, useHttpClient } from 'app/src/shared/hooks'
import { MenuItem } from 'primereact/menuitem'
import { Toast, ToastMessage } from 'primereact/toast'
import { createContext, RefObject, useEffect, useRef, useState } from 'react'
import { ConsoleLogger } from './logging'
import { ILogger } from './logging/loggingutils'
import { initStyles, updateStyles } from './styleutils'

type WebAppInitContextType = object

export type MealVouchersStep = 'wallet' | 'transactions' | 'selectShop' | 'selectQuantity' | 'payment'

type WebAppInnerInitContextType = {
  httpClient: HttpClientType
  toastMessagesRef: RefObject<Toast>
  userInfo: membershipApi.UserInfo & { isForbidden: boolean; canSelfRegister: boolean }
  portalConfiguration: portalConfigurationApi.PortalConfig
  sessionExpired: boolean
  mealVouchers: {
    currentStep: MealVouchersStep
    selectedVendor?: mealVouchersApi.UnitMerchant
    favouriteVendors?: mealVouchersApi.UnitMerchant[]
    steps: MealVouchersStep[]
    activeTab: number
    nextStep: (currentStep: MealVouchersStep) => MealVouchersStep
    prevStep: (currentStep: MealVouchersStep) => MealVouchersStep
    wallet: {
      totalAmountSelected?: number
      vouchers: MealVoucher[]
    }
    otp?: string
    showSuccessStep?: boolean
    showErrorStep?: boolean
    accessToken?: string
    loginInfo?: {
      accessToken?: string
      companyCode?: string
      userInfo?: string
    }
  }
} & WebAppInitContextType

type WebAppInnerContextType = {
  logger?: ILogger
  httpClient?: HttpClientType
  showContactUs: boolean | undefined
  showUserSidePanel: boolean | undefined
  showCompanySelectionDialog?: boolean
  breadCrumbItems: MenuItem[]
  toastMessagesRef: RefObject<Toast>
  showToastMessage: (message: ToastMessage) => void
  userInfo: membershipApi.UserInfo & { isForbidden: boolean; canSelfRegister: boolean }
  portalConfiguration: portalConfigurationApi.PortalConfig
  sessionExpired: boolean
  mealVouchers: {
    currentStep: MealVouchersStep
    selectedVendor?: mealVouchersApi.UnitMerchant
    favouriteVendors?: mealVouchersApi.UnitMerchant[]
    steps: MealVouchersStep[]
    activeTab: number
    nextStep: (currentStep: MealVouchersStep) => MealVouchersStep
    prevStep: (currentStep: MealVouchersStep) => MealVouchersStep
    wallet: {
      totalAmountSelected?: number
      vouchers: MealVoucher[]
    }
    otpInfo?: mealVouchersApi.PosOtpCreateResponse
    showSuccessStep?: boolean
    showErrorStep?: boolean
    loginInfo?: {
      accessToken?: string
      companyCode?: string
      userId?: string
    }
  }
}

export type WebAppContextType = {
  update: (partialCtx: Partial<WebAppInnerContextType>) => void
} & WebAppInnerContextType

enum ReadyState {
  NOTREADY = 0,
  INPROGRESS = 1,
  READY = 2,
}

async function buildContext(_props: WebAppInnerInitContextType): Promise<WebAppInnerContextType> {
  let showCompanyDialog = false
  // console.log('buildContext', _props)

  if (!_props.userInfo.isForbidden && !_props.userInfo.canSelfRegister) {
    await initStyles(_props.userInfo?.welfareCampaign?.configuration?.frontend)
    let localStorageCompaign = getFromSessionStorage('selectedCampaign')
    console.log('localStorageCampaign', localStorageCompaign)
    console.log('userInfo', _props.userInfo)
    console.log('expired', _props.sessionExpired)
    const existCampaign = _props.userInfo?.userMemberships.some(
      (m: membershipApi.UserMembership) => m.membershipId === JSON.parse(localStorageCompaign || '{}')?.membershipId,
    )
    if (!existCampaign) {
      localStorageCompaign = null
      localStorage.removeItem('selectedCampaign')
    }
    // Verifica se selectedCampaign esiste: true se esiste, false se non esiste
    const hasSelectedCampaign = localStorageCompaign !== null && localStorageCompaign !== undefined && localStorageCompaign !== ''
    if (!hasSelectedCampaign && activeCampaigns(_props.userInfo.userMemberships).length === 1) {
      setToSessionStorage('selectedCampaign', JSON.stringify(activeCampaigns(_props.userInfo.userMemberships)[0]))
    }

    showCompanyDialog = !hasSelectedCampaign && activeCampaigns(_props.userInfo.userMemberships).length >= 2
    const noActiveCampaigns = !hasSelectedCampaign && activeCampaigns(_props.userInfo.userMemberships).length === 0
    if (noActiveCampaigns) {
      const lastCampaign = _props.userInfo.userMemberships.find(
        (c) => c.campaignId === _props.userInfo.welfareCampaign?.campaignId,
      )
      setToSessionStorage('selectedCampaign', JSON.stringify(lastCampaign!))
    }
  } else {
    // prendi lo stile di default
    // assegna a userInfo una campagna fittizia con templateId DEFAULT
    /*_props.userInfo.welfareCampaign = {
      campaignName: 'Default Campaign',
      phase: 'pre-join',
      companyCode: 'DEFAULT_CODE',
      companyName: 'Default Company',
      documents: [],
      fruitionDateFrom: new Date().toISOString(),
      fruitionDateTo: new Date().toISOString(),
      mustJoin: false,

      configuration: {
        frontend: {
          companyLogoPath: '',
          cssPath: 'css/fallback.css',
          templateId: 'DEFAULT',
        },
        funnels: [],
        familyMemberPrivacyDocument: false,
        familyMembersRelations: [],
      },
    }*/
    await initStyles(_props.userInfo?.welfareCampaign?.configuration?.frontend)
  }
  let logger: ILogger
  if (import.meta.env.MODE === 'development') {
    logger = new ConsoleLogger()
  } else {
    logger = new ConsoleLogger()
  }

  return {
    httpClient: _props.httpClient,
    logger,
    showContactUs: false,
    showUserSidePanel: false,
    breadCrumbItems: [],
    showCompanySelectionDialog: showCompanyDialog,
    toastMessagesRef: _props.toastMessagesRef,
    showToastMessage: (message: ToastMessage) => _props.toastMessagesRef?.current?.show(message),
    userInfo: _props.userInfo,
    portalConfiguration: _props.portalConfiguration,
    sessionExpired: _props.sessionExpired,
    mealVouchers: _props.mealVouchers,
  }
}

export const WebAppContextProvider = (props: React.PropsWithChildren<{ ctxInit?: WebAppInitContextType }>) => {
  const httpClient = useHttpClient()
  const toastMessagesRef = useRef<Toast>(null)
  const isInitializedRef = useRef<boolean>(false)
  const [ready, setReady] = useState<ReadyState>(ReadyState.NOTREADY)
  const [ctx, setCtx] = useState<WebAppInnerContextType>()

  useEffect(() => {
    if (ready === ReadyState.NOTREADY) {
      setReady(ReadyState.INPROGRESS)
    }
  }, [ready])

  useEffect(() => {
    const workerFunc = async () => {
      const userInfoJsonResp = await httpClient.executeJson<membershipApi.UserInfo>({
        input: 'membership/api/v1/users/info',
      })
      console.log('userInfoJsonResp:', userInfoJsonResp)

      const portalJsonResp = await httpClient.executeJson<portalConfigurationApi.PortalConfig>({
        input: 'api/v1/portalconfiguration',
      })

      console.log('portalJsonResp:', portalJsonResp)

      // Verifica se ci troviamo nel caso di registrazione richiesta
      const registrationRequired = userInfoJsonResp.fetchError?.message === 'REGISTRATION_REQUIRED'

      const ctxResp = await buildContext({
        ...props.ctxInit,
        httpClient,
        toastMessagesRef,
        userInfo: {
          ...userInfoJsonResp.jsonResponse!,
          isForbidden: userInfoJsonResp.httpResponse?.status === 403,
          canSelfRegister: registrationRequired,
        },
        portalConfiguration: portalJsonResp.jsonResponse!,
        sessionExpired: false,
        mealVouchers: {
          currentStep: 'wallet',
          steps: ['wallet', 'selectShop', 'selectQuantity', 'payment'],
          activeTab: 0,
          nextStep: (currentStep: MealVouchersStep) => {
            const currentIndex = ctxResp.mealVouchers?.steps?.indexOf(currentStep) || 0
            return ctxResp.mealVouchers?.steps?.[currentIndex + 1] || null
          },
          prevStep: (currentStep: MealVouchersStep) => {
            const currentIndex = ctxResp.mealVouchers?.steps?.indexOf(currentStep) || 0
            return ctxResp.mealVouchers?.steps?.[currentIndex - 1] || null
          },
          wallet: {
            totalAmountSelected: 0,
            vouchers: [
              {
                quantity: 0,
                amount: 0,
                expiryDate: '',
                selectedQuantity: 0,
                assetCode: '',
              },
            ],
          },
        },
      })
      setCtx(ctxResp)
    }
    if (httpClient && ready === ReadyState.INPROGRESS && !ctx) workerFunc()
  }, [httpClient, props.ctxInit, ready, ctx])

  useEffect(() => {
    if (ctx) setReady(ReadyState.READY)
  }, [ctx])

  // Monitora i cambiamenti del cssPath e aggiorna il CSS dinamicamente
  useEffect(() => {
    const cssPath = ctx?.userInfo?.welfareCampaign?.configuration?.frontend?.cssPath
    const frontend = ctx?.userInfo?.welfareCampaign?.configuration?.frontend

    if (cssPath && frontend && ready === ReadyState.READY) {
      // Salta la prima esecuzione (CSS già caricato da initStyles)
      if (!isInitializedRef.current) {
        isInitializedRef.current = true
        return
      }

      updateStyles(frontend)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.userInfo?.welfareCampaign?.configuration?.frontend?.cssPath, ready])

  const updateContext = (partialCtx: Partial<WebAppInnerContextType>) => {
    setCtx((c) => ({ ...c!, ...partialCtx }))
  }

  return (
    ready === ReadyState.READY && (
      <WebAppContext.Provider value={{ ...ctx!, update: updateContext }}>{props.children}</WebAppContext.Provider>
    )
  )
}
export const WebAppContext = createContext<WebAppContextType | undefined>(undefined)
